'use client';

import { MessageSquare, Highlighter, Strikethrough, Trash2 } from 'lucide-react';
import { ViewerAnnotation } from './types';

interface AnnotationPanelProps {
  annotations: ViewerAnnotation[];
  onDeleteAnnotation: (id: string) => void;
  onAnnotationClick: (annotation: ViewerAnnotation) => void;
}

export function AnnotationPanel({ annotations, onDeleteAnnotation, onAnnotationClick }: AnnotationPanelProps) {
  // Sort by creation time (newest first)
  const sortedAnnotations = [...annotations].sort((a, b) => b.createdAt - a.createdAt);

  const getIcon = (type: ViewerAnnotation['type']) => {
    switch (type) {
      case 'comment':
        return <MessageSquare className="w-4 h-4 text-yellow-500" />;
      case 'highlight':
        return <Highlighter className="w-4 h-4 text-primary" />;
      case 'deletion':
        return <Strikethrough className="w-4 h-4 text-red-400" />;
    }
  };

  const getTypeBadge = (type: ViewerAnnotation['type']) => {
    const styles = {
      comment: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
      highlight: 'bg-primary/10 text-primary border-primary/30',
      deletion: 'bg-red-500/10 text-red-400 border-red-500/30',
    };

    const labels = {
      comment: 'Comment',
      highlight: 'Highlight',
      deletion: 'Deletion',
    };

    return (
      <span className={`text-xs px-1.5 py-0.5 rounded border ${styles[type]}`}>
        {labels[type]}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col bg-bg-card border-l border-border" data-annotation-panel>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-text">Annotations</h3>
          <span className="text-xs px-2 py-0.5 bg-bg-alt rounded-full text-text-muted">
            {annotations.length}
          </span>
        </div>
      </div>

      {/* Annotations List */}
      <div className="flex-1 overflow-y-auto">
        {sortedAnnotations.length === 0 ? (
          <div className="p-4 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-bg-alt flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-text-muted" />
            </div>
            <p className="text-sm text-text-muted">No annotations yet</p>
            <p className="text-xs text-text-muted mt-1">Select text to add comments or highlights</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedAnnotations.map((annotation) => (
              <div
                key={annotation.id}
                onClick={() => onAnnotationClick(annotation)}
                className="p-3 hover:bg-bg-alt/50 cursor-pointer transition-colors group"
              >
                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                  {getIcon(annotation.type)}
                  {getTypeBadge(annotation.type)}
                  <span className="text-xs text-text-muted ml-auto">
                    {formatTime(annotation.createdAt)}
                  </span>
                </div>

                {/* Selected text preview */}
                <p className="text-sm text-text-secondary mb-1.5 line-clamp-2">
                  &ldquo;{annotation.text}&rdquo;
                </p>

                {/* Comment (if any) */}
                {annotation.comment && (
                  <p className="text-sm text-text pl-2 border-l-2 border-yellow-500/50">
                    {annotation.comment}
                  </p>
                )}

                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteAnnotation(annotation.id);
                  }}
                  className="mt-2 flex items-center gap-1 text-xs text-text-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash2 className="w-3 h-3" />
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(timestamp).toLocaleDateString();
}
